import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import type { Connection } from '@/types';

interface ConnectionListProps {
  connections: Connection[];
  onDisconnect: (connectionId: string) => void;
}

export default function ConnectionList({ connections, onDisconnect }: ConnectionListProps) {
  if (connections.length === 0) {
    return (
      <div style={styles.empty}>
        <p style={styles.emptyText}>No active connections</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <table style={styles.table}>
        <thead>
          <tr style={styles.headerRow}>
            <th style={styles.th}>Room</th>
            <th style={styles.th}>User ID</th>
            <th style={styles.th}>Language</th>
            <th style={styles.th}>Connected</th>
            <th style={styles.th}>Latency</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {connections.map((connection) => (
            <tr key={connection.id} style={styles.row}>
              <td style={styles.td}>
                <span style={styles.roomBadge}>Room {connection.roomId}</span>
              </td>
              <td style={styles.td}>
                <code style={styles.code}>
                  {connection.userId.slice(0, 8)}...
                </code>
              </td>
              <td style={styles.td}>
                <span style={styles.languageBadge}>{connection.language}</span>
              </td>
              <td style={styles.td}>
                {formatDistanceToNow(new Date(connection.connectedAt), {
                  addSuffix: true,
                })}
              </td>
              <td style={styles.td}>
                <span
                  style={{
                    ...styles.latencyBadge,
                    backgroundColor: getLatencyColor(connection.latency),
                  }}
                >
                  {connection.latency}ms
                </span>
              </td>
              <td style={styles.td}>
                <span
                  style={{
                    ...styles.statusBadge,
                    backgroundColor: getStatusColor(connection.status),
                  }}
                >
                  {connection.status}
                </span>
              </td>
              <td style={styles.td}>
                <button
                  onClick={() => onDisconnect(connection.id)}
                  style={styles.disconnectButton}
                >
                  Disconnect
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function getLatencyColor(latency: number): string {
  if (latency < 200) return '#48bb78'; // Green
  if (latency < 500) return '#ed8936'; // Orange
  return '#f56565'; // Red
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'connected':
      return '#48bb78';
    case 'disconnected':
      return '#a0aec0';
    case 'error':
      return '#f56565';
    default:
      return '#cbd5e0';
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  headerRow: {
    backgroundColor: '#f7fafc',
  },
  th: {
    padding: '12px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '600',
    color: '#4a5568',
    textTransform: 'uppercase',
    borderBottom: '2px solid #e2e8f0',
  },
  row: {
    borderBottom: '1px solid #e2e8f0',
  },
  td: {
    padding: '12px',
    fontSize: '14px',
    color: '#2d3748',
  },
  roomBadge: {
    padding: '4px 10px',
    backgroundColor: '#667eea',
    color: 'white',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  code: {
    fontFamily: 'monospace',
    fontSize: '13px',
    color: '#4a5568',
    backgroundColor: '#f7fafc',
    padding: '2px 6px',
    borderRadius: '4px',
  },
  languageBadge: {
    padding: '4px 10px',
    backgroundColor: '#e6fffa',
    color: '#234e52',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  latencyBadge: {
    padding: '4px 10px',
    color: 'white',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  statusBadge: {
    padding: '4px 10px',
    color: 'white',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  disconnectButton: {
    padding: '6px 12px',
    backgroundColor: '#f56565',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  empty: {
    padding: '40px',
    textAlign: 'center',
  },
  emptyText: {
    fontSize: '16px',
    color: '#718096',
    margin: 0,
  },
};
